<script>
    import JSONTree from 'svelte-json-tree';
    import CodeEditor from './CodeEditor.svelte';
    import {notifier} from '@beyonk/svelte-notifications';
    import { debounce } from '../../Utilities/debounce';
    import Collapse from '../Collapse.svelte';
    import Loader from '../Loader.svelte';
    import {confirm} from '../Confirmation.svelte';
    import ReportComments from './ReportComments.svelte';
    import { generate_report } from '../../lib/report_utils';

    import {engine} from '../../lib/liquid_engine';

    // Font Awesome
    import Fa from 'svelte-fa';
    import { faFloppyDisk, faBan } from '@fortawesome/free-solid-svg-icons';

    export let params;

    export let assessment;
    export let assessment_model;

    let report_generating;
    let slideBG;
    let error_message = "";
    let rendered_tpl;
    let rendered_css;

    let slide;
    let body;
    let css;

    let metadata = {
        type: "ppt",
        slide_no: 1
    }

    async function setSlideBackground () {
        if (!(slide_idx != undefined && slideBG && $assessment)) return;

        try {
            slideBG.contentDocument.getElementById("global_css").innerHTML = $assessment.assessment_type_data.global_css;
            slideBG.contentDocument.getElementById("custom_css").innerHTML = rendered_css;

            if (slideBG.contentDocument.getElementsByTagName("body")[0].innerHTML != rendered_tpl) {
                slideBG.contentDocument.getElementsByTagName("body")[0].innerHTML = rendered_tpl;
            }

            slideBG.contentWindow.render(slide_idx + 1);
        } catch (e) {}

        error_message = "";
    }

    async function render() {

        if (!slide) return;

        if ($assessment.slides[slide.slide_id] && !$assessment.slides[slide.slide_id].reset) {
            rendered_tpl = $assessment.slides[slide.slide_id].body;
            rendered_css = $assessment.slides[slide.slide_id].css;
        } else {
            rendered_tpl = " <h1>Loading...</h1>";
            rendered_css = "Loading..."
            try {
                rendered_tpl = await engine.parseAndRender(body, {...$assessment, slide_number: params.slide_no});
                rendered_css = await engine.parseAndRender(css, {...$assessment, slide_number: params.slide_no});
            } catch (e) {
                error_message = e.message;
                return
            }
        }

        error_message = "";
    }

    function saveSlide() {
        let custom_slide = $assessment.slides[slide.slide_id] || {}

        custom_slide.slide_id = slide.slide_id;
        custom_slide.css = rendered_css;
        custom_slide.reset = false;
        custom_slide.body = rendered_tpl;

        assessment_model.saveSlide(custom_slide).then(() => {
            notifier.success("Slide saved");
        });
    }

    function excludeSlide() {
        let custom_slide = $assessment.slides[slide.slide_id] || {}

        custom_slide.slide_id = slide.slide_id;
        custom_slide.css = rendered_css;
        custom_slide.reset = false;
        custom_slide.exclude = true;
        custom_slide.body = rendered_tpl;

        assessment_model.saveSlide(custom_slide).then(() => {
            notifier.success("Slide excluded");
        });
    }

    function resetSlide() {
        let custom_slide = $assessment.slides[slide.slide_id];
        if (custom_slide) {
            custom_slide.reset = true;
            custom_slide.exclude = false;
            assessment_model.saveSlide(custom_slide);
        }
    }

    function resetAllSlides() {
        confirm("Are you sure you want to reset all slides?").then(() => {
            let promises = [];
            for (let s of $assessment.assessment_type_data.report_slides) {
                let custom_slide = $assessment.slides[s.slide_id];
                if (custom_slide) {
                    custom_slide.reset = true;
                    custom_slide.exclude = false;

                    promises.push(assessment_model.saveSlide(custom_slide));
                }
            }

            Promise.all(promises).then(() => {
                notifier.success("All Slides Reset");
            });
        }).catch(() => null);
    }

    function addListener() {
        setSlideBackground();
        let body_el = slideBG.contentDocument.getElementsByTagName("body")[0]

        const debounced_function = debounce(() => {
            rendered_tpl = body_el.innerHTML;
        }, 500)

        body_el.addEventListener("input", () => {
            debounced_function();
        })
    }

    async function generate() {
        report_generating = 0;
        let i = setInterval(() => {
            report_generating += 1;
        }, 1000);
        await generate_report($assessment);
        clearInterval(i);
        report_generating = undefined;
    }

    function changeSlide(idx) {
        slide = $assessment.assessment_type_data.report_slides[idx];
        body = slide?.body;
        css = slide?.css;

        metadata.slide_no = params.slide_no;
    }

    $: slide_idx = params.slide_no - 1;
    $: changeSlide(slide_idx);

    $: render(slide, $assessment)
    $: setSlideBackground(rendered_tpl, rendered_css);
</script>

<div class="container is-fluid">
    <Loader loading={report_generating != undefined} message="Generating Report... {report_generating}s">
        <div class="columns">
            <div class="column is-narrow">
                Slides:
                <ul class="slides">
                    {#each $assessment.assessment_type_data.report_slides as slide, idx}
                        <li><a href="#/assessment/{params.id}/Report/ppt/{idx+1}" class="slide" class:selected={idx + 1 == params.slide_no}>{idx + 1}</a></li>
                    {/each}
                </ul>
            </div>
            <div class="column">
                {#if slide}
                <div class="preview">
                    <div class="level">
                        <div class="level-left">
                            <div class="level-item">
                                <div class="buttons">
                                    <button class="button is-success" on:click={saveSlide}>Save Slide</button>
                                    <button class="button is-warning" on:click={excludeSlide}>Exclude Slide</button>
                                </div>
                            </div>
                        </div>
                        <div class="level-item">
                            {#if $assessment.slides[slide.slide_id] && $assessment.slides[slide.slide_id].exclude}
                                <span class="icon has-text-danger" alt="Slide is excluded"><Fa icon={faBan}></Fa></span>
                            {:else if $assessment.slides[slide.slide_id] && !$assessment.slides[slide.slide_id].reset}
                                <span class="icon" alt="Slide is Saved"><Fa icon={faFloppyDisk}></Fa></span>
                            {/if}
                        </div>
                        <div class="level-right">
                            <div class="level-item">
                                <div class="buttons">
                                    <button class="button is-danger" on:click={resetAllSlides}>Reset All Slides</button>
                                    <button class="button is-warning" on:click={resetSlide}>Reset Slide</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="slide">
                        <iframe src="/slide_preview.html" bind:this={slideBG} title="Slide Background" on:load={addListener}></iframe>
                    </div>
                    <div>
                        <span class="has-text-error">{error_message}</span>
                    </div>
                    <div class="buttons">
                        <button class="button is-success is-pulled-right" on:click={generate}>Generate Report</button>
                    </div>
                </div>
                <Collapse title="Show Source">
                    <div class="columns source">
                        <div class="column is-4">
                            {#if rendered_tpl}
                                <CodeEditor lang="html" bind:value={rendered_tpl} />
                            {/if}
                        </div>
                        <div class="column is-4">
                            {#if rendered_css}
                            <CodeEditor lang="css" bind:value={rendered_css} />
                            {/if}
                        </div>
                        <div class="column is-4 json">
                            <JSONTree value={$assessment}></JSONTree>
                        </div>
                    </div>
                </Collapse>
                {/if}
            </div>
            <div class="column is-3 comments">
                <ReportComments {assessment} {assessment_model} {metadata} />
            </div>
        </div>
    </Loader>
</div>

<style>
    .slide {
        border: 1px solid gray;
        height: 7.52in;
        width: calc(13.333in + 19px);
        position: relative;
        margin-top: 1em;
        margin-bottom: 1em;
    }

    .source {
        margin-top: 1em;
    }

    .source .column {
        height: 450px;
        overflow: auto;
    }
    a.slide {
        display: block;
        height: 2em;
        width: 3em;
        border: 1px solid lightgray;
        cursor: pointer;
        text-align: center;
        margin-bottom: .5em;
    }

    a.slide.selected {
        background-color: lightcyan;
    }

    .json :global(.container) {
        padding: 0;
    }
    iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
    }

    .preview {
        margin-top: 10px;
    }


    .comments {
        display: grid;
        grid-template-rows: min-content minmax(auto, calc(100vh - 320px)) min-content;
    }
</style>